import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { PasswordRequirements } from '@components/molecules/PasswordRequirements';
import { Card } from '@components/atoms/Card';
import { FormikProps } from 'formik';
import { useHistory } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import RegistrationWizard from '../RegistrationWizard';
import PlanStep from '../PlanStep';
import PaymentMethodSelector from '../PaymentMethodSelector';
import { useSubscriptionPlans } from '@hooks/api/subscription/useSubscriptionPlans';
import { PriceSummary } from '@components/organisms/subscription';
import { PaymentMethodType } from '@models/subscription';
import { track } from '@config/analytics';
import { useTranslation } from '@hooks/useTranslation';

const toStepKey = (title: string) => title.toLowerCase().replace(/\s+/g, '_');

/**
 * Sign-up form values.
 *
 * Payment fields are present but only used when the selected plan sets
 * `requiresPaymentUpfront`. A card-free plan submits without any of them.
 */
export interface RegisterFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  organizationType: string; // 'create' or 'join'
  organizationName: string;
  planId: string;
  selectedPlanId: string;
  selectedPlanName: string;
  requiresCard: boolean;
  paymentMethodType: PaymentMethodType | '';
  billingPeriod: string;
  billingFirstName: string;
  billingLastName: string;
  billingEmail: string;
  billingAddress1: string;
  billingAddress2: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry: string;
  billingPhone: string;
  ccNumber: string;
  ccExp: string;
  cvv: string;
}

export interface RegisterFormProps {
  formik: FormikProps<RegisterFormValues>;
}

/** One sentence describing what the chosen plan actually does at sign-up. */
const planSummary = (
  plan: {
    name: string;
    trialDurationDays?: number;
    requiresPaymentUpfront?: boolean;
  },
  t: {
    trialPlanSummary: string;
    billedImmediatelyPlanSummary: string;
    noCardRequiredHint: string;
    cardRequiredHint: string;
  },
) => {
  const hasTrial = Boolean(plan.trialDurationDays && plan.trialDurationDays > 0);
  const trial = hasTrial
    ? t.trialPlanSummary
        .replace('{days}', String(plan.trialDurationDays))
        .replace('{plan}', plan.name)
    : t.billedImmediatelyPlanSummary.replace('{plan}', plan.name);
  const card = plan.requiresPaymentUpfront === false ? ` ${t.noCardRequiredHint}` : ` ${t.cardRequiredHint}`;

  return `${trial}${card}`;
};

const RegisterForm: React.FC<RegisterFormProps> = ({ formik }) => {
  // Sprint 28, story-143. Bucket 1 (pre-auth) — see docs/architecture.md "## Sprint 28" Decision 4.
  const tLogin = useTranslation('login');
  const history = useHistory();

  // Public, non-add-on plans, cheapest first. The resolver is public, so this loads for an
  // unauthenticated visitor on /register.
  const {
    data: allPlans,
    isLoading: isLoadingPlans,
    isError: isPlansError,
  } = useSubscriptionPlans();

  const plans = useMemo(
    () =>
      (allPlans ?? [])
        .filter((plan) => plan.isPublic && !plan.isAddOn)
        .sort((a, b) => a.basePriceCents - b.basePriceCents),
    [allPlans],
  );

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === formik.values.planId) ?? null,
    [plans, formik.values.planId],
  );

  const { setFieldValue } = formik;
  const { planId, requiresCard } = formik.values;

  // With exactly one public plan there is nothing to choose, so adopt it. This is not a
  // default: it is the only thing on offer, and the user is shown it before continuing.
  useEffect(() => {
    if (plans.length === 1 && !planId) {
      setFieldValue('planId', plans[0].id);
    }
  }, [plans, planId, setFieldValue]);

  // PaymentMethodSelector prices its Monthly/Annual toggle off its own `selectedPlanId`
  // field, falling back to the first public plan when unset. Left unsynced it would quote
  // the cheapest plan's price no matter which one was chosen.
  useEffect(() => {
    setFieldValue('selectedPlanId', planId);
  }, [planId, setFieldValue]);

  // Mirror of the selected plan's display *name*, the same shape as the selectedPlanId
  // mirror above — GA4's `sign_up_completed` needs a human-readable plan name, never an id.
  useEffect(() => {
    setFieldValue('selectedPlanName', selectedPlan?.name ?? '');
  }, [selectedPlan, setFieldValue]);

  // Keep the card rules in step with the chosen plan. paymentMethodType is cleared for a
  // card-free plan so PaymentMethodSelector's own card rules stay dormant too.
  useEffect(() => {
    const planRequiresCard = selectedPlan?.requiresPaymentUpfront === true;
    if (planRequiresCard !== requiresCard) {
      setFieldValue('requiresCard', planRequiresCard);
      setFieldValue(
        'paymentMethodType',
        planRequiresCard ? PaymentMethodType.CREDIT_CARD : '',
      );
    }
  }, [selectedPlan, requiresCard, setFieldValue]);

  // sign_up_step {step: 'personal_info'} — "join" has no wizard (renders the personal-info
  // step directly), so this is its only step-display event. One-shot, guarded by a ref so
  // it fires exactly once the first time this branch is entered, not on every re-render.
  const firedJoinStep = useRef(false);
  useEffect(() => {
    if (formik.values.organizationType === 'join' && !firedJoinStep.current) {
      firedJoinStep.current = true;
      track('sign_up_step', { step: 'personal_info' });
    }
  }, [formik.values.organizationType]);

  // sign_up_step {step: 'plan'} — the step-0-on-mount case RegistrationWizard itself cannot
  // provide (onStepChange only fires on next/prev). One-shot on first entry to "create".
  const firedCreateStep = useRef(false);
  useEffect(() => {
    if (formik.values.organizationType === 'create' && !firedCreateStep.current) {
      firedCreateStep.current = true;
      track('sign_up_step', { step: 'plan' });
    }
  }, [formik.values.organizationType]);

  // sign_up_type_selected {type} — the type-choice radios unmount the instant a value is
  // picked (renderForm branches away from this block), so a native radio's own onChange
  // firing once per distinct selection is all the dedup this needs.
  const handleOrganizationTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    track('sign_up_type_selected', { type: e.target.value as 'create' | 'join' });
    formik.handleChange(e);
  };

  // Fires on every *display* of a step, not only forward progress (back navigation re-fires
  // the step it lands on) — logging behavior only, no UI change. `stepTitles[index]` (this
  // component's own array) rather than a hard-coded switch means the `payment` step needs no
  // special-casing: it only ever appears in `stepTitles` when `requiresCard` is true.
  const handleStepChange = (index: number, stepTitles: string[]) => {
    const step = stepTitles[index];
    if (step) track('sign_up_step', { step: toStepKey(step) });
  };

  // Handle form submission - prevent accidental submits
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Only submit if it's explicitly triggered (via button click in wizard or join form)
    // The wizard will call formik.handleSubmit() directly
  };

  // Render Personal Information Step
  const renderPersonalInfoStep = () => (
    <div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        {tLogin.personalInformation}
      </h2>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormGroup label={tLogin.firstNameLabel} htmlFor="firstName">
            <Input
              type="text"
              name="firstName"
              autoComplete="given-name"
              value={formik.values.firstName}
              placeholder={tLogin.firstNamePlaceholder}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.firstName && formik.errors.firstName)}
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <p className="text-sm text-red-500 mt-1">{formik.errors.firstName}</p>
            )}
          </FormGroup>

          <FormGroup label={tLogin.lastNameLabel} htmlFor="lastName">
            <Input
              type="text"
              name="lastName"
              autoComplete="family-name"
              value={formik.values.lastName}
              placeholder={tLogin.lastNamePlaceholder}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.lastName && formik.errors.lastName)}
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <p className="text-sm text-red-500 mt-1">{formik.errors.lastName}</p>
            )}
          </FormGroup>
        </div>

        <FormGroup label={tLogin.email} htmlFor="email">
          <Input
            type="email"
            name="email"
            autoComplete="email"
            value={formik.values.email}
            placeholder={tLogin.enterEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.email && formik.errors.email)}
          />
          {formik.touched.email && formik.errors.email && (
            <p className="text-sm text-red-500 mt-1">{formik.errors.email}</p>
          )}
        </FormGroup>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormGroup label={tLogin.password} htmlFor="password">
            <Input
              type="password"
              name="password"
              autoComplete="new-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.password && formik.errors.password)}
              placeholder={tLogin.enterPassword}
            />
          </FormGroup>

          <FormGroup label={tLogin.confirmPasswordLabel} htmlFor="confirmPassword">
            <Input
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
              placeholder={tLogin.confirmYourPassword}
            />
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <p className="text-sm text-red-500 mt-1">{formik.errors.confirmPassword}</p>
            )}
          </FormGroup>
        </div>

        <PasswordRequirements value={formik.values.password} />
      </div>
    </div>
  );

  // Render Organization Information Step
  const renderOrganizationInfoStep = () => (
    <div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        {tLogin.organizationInformation}
      </h2>
      <div className="flex flex-col gap-4">
        <FormGroup label={tLogin.organizationNameLabel} htmlFor="organizationName">
          <Input
            type="text"
            name="organizationName"
            autoComplete="organization"
            value={formik.values.organizationName || ''}
            placeholder={tLogin.organizationNamePlaceholder}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.organizationName && formik.errors.organizationName)}
          />
          {formik.touched.organizationName && formik.errors.organizationName && (
            <p className="text-sm text-red-500 mt-1">{formik.errors.organizationName}</p>
          )}
        </FormGroup>

        {selectedPlan && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {planSummary(selectedPlan, tLogin)}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Card + billing address. Plan selection is off because it has its own step, and
  // PaymentMethodSelector would otherwise auto-pick the first plan for the user.
  const renderPaymentStep = () => (
    <div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
        {tLogin.paymentDetailsStep}
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {tLogin.planRequiresCard.replace('{plan}', selectedPlan?.name || '')}
      </p>
      <PaymentMethodSelector
        formik={formik}
        userData={{
          firstName: formik.values.firstName,
          lastName: formik.values.lastName,
          email: formik.values.email,
        }}
        showPlanSelection={false}
        showBillingPeriod
      />

      {selectedPlan && (
        <div className="mt-6">
          <PriceSummary
            amount={
              (selectedPlan.basePriceCents / 100) *
              (formik.values.billingPeriod === 'ANNUAL' ? 12 : 1)
            }
            billingPeriod={
              formik.values.billingPeriod === 'ANNUAL' ? 'ANNUAL' : 'MONTHLY'
            }
            planName={selectedPlan.name}
          />
        </div>
      )}
    </div>
  );


  // Render form based on organization type
  const renderForm = () => {
    // If organization type is not selected, show selection first
    if (!formik.values.organizationType) {
      return (
        <div className="flex flex-col gap-6">
          {/* Organization Type Selection - Show First */}
          <div>
            <label className="block text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {tLogin.howWouldYouLikeToStart} <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-col gap-4">
              {/* Join Existing Organization Option */}
              <label
                className={`flex items-start p-5 border-2 rounded-lg cursor-pointer transition-all ${
                  formik.values.organizationType === 'join'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <input
                  type="radio"
                  name="organizationType"
                  value="join"
                  checked={formik.values.organizationType === 'join'}
                  onChange={handleOrganizationTypeChange}
                  onBlur={formik.handleBlur}
                  className="mt-1 mr-4 w-4 h-4 flex-shrink-0"
                />
                <div className="flex-1">
                  <p className="font-semibold text-base text-gray-900 dark:text-white mb-1">
                    {tLogin.invitedOptionTitle}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {tLogin.invitedOptionDesc}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      {tLogin.noPaymentRequired}
                    </span>
                  </div>
                </div>
              </label>

              {/* Create New Organization Option */}
              <label
                className={`flex items-start p-5 border-2 rounded-lg cursor-pointer transition-all ${
                  formik.values.organizationType === 'create'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <input
                  type="radio"
                  name="organizationType"
                  value="create"
                  checked={formik.values.organizationType === 'create'}
                  onChange={handleOrganizationTypeChange}
                  onBlur={formik.handleBlur}
                  className="mt-1 mr-4 w-4 h-4 flex-shrink-0"
                />
                <div className="flex-1">
                  <p className="font-semibold text-base text-gray-900 dark:text-white mb-1">
                    {tLogin.createOptionTitle}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {tLogin.createOptionDesc}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      {tLogin.choosePlanNext}
                    </span>
                  </div>
                </div>
              </label>
            </div>
            {formik.touched.organizationType && formik.errors.organizationType && (
              <p className="text-sm text-red-500 mt-2">{formik.errors.organizationType}</p>
            )}
          </div>

          {/* Continue Button */}
          <Button
            type="button"
            variant="primary"
            fullWidth
            disabled={!formik.values.organizationType}
            onClick={() => {
              if (formik.values.organizationType) {
                // Just trigger validation, form will re-render
                formik.setFieldTouched('organizationType', true);
              }
            }}
          >
            {tLogin.continueButton}
          </Button>
        </div>
      );
    }

    // Si seleccionó "join", mostrar form simple
    if (formik.values.organizationType === 'join') {
      return (
        <div className="flex flex-col gap-4">
          {renderPersonalInfoStep()}

          <Button
            type="button"
            variant="primary"
            fullWidth
            className="mt-6"
            onClick={() => formik.handleSubmit()}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? tLogin.creatingAccount : tLogin.createAccount}
          </Button>
        </div>
      );
    }

    // "create": the step list is derived from the chosen plan — picking a card-required
    // plan grows the wizard by a payment step.
    if (formik.values.organizationType === 'create') {
      const stepTitles = [tLogin.planStepTitle, tLogin.personalInfoStepTitle, tLogin.organizationStepTitle];
      const steps = [
        <PlanStep
          key="plan"
          plans={plans}
          selectedPlanId={formik.values.planId}
          onSelectPlan={(id) => setFieldValue('planId', id)}
          isLoading={isLoadingPlans}
          isError={isPlansError}
        />,
        renderPersonalInfoStep(),
        renderOrganizationInfoStep(),
      ];

      if (requiresCard) {
        stepTitles.push(tLogin.paymentStepTitle);
        steps.push(renderPaymentStep());
      }

      const canAdvance = (step: number) => {
        // Plan step: an explicit choice is required — there is no default to fall back on.
        if (step === 0) return Boolean(formik.values.planId);
        // Personal info
        if (step === 1) {
          return (
            !formik.errors.firstName &&
            !formik.errors.lastName &&
            !formik.errors.email &&
            !formik.errors.password &&
            !formik.errors.confirmPassword &&
            Boolean(formik.values.email && formik.values.password)
          );
        }
        // Organization
        if (step === 2) {
          return (
            Boolean(formik.values.organizationName) &&
            !formik.errors.organizationName
          );
        }
        return true;
      };

      return (
        <RegistrationWizard
          formik={formik}
          stepTitles={stepTitles}
          onStepChange={(index) => handleStepChange(index, stepTitles)}
          canAdvance={canAdvance}
          submitLabel={
            selectedPlan?.trialDurationDays
              ? tLogin.createAccountAndStartTrial
              : tLogin.createAccount
          }
        >
          {steps}
        </RegistrationWizard>
      );
    }

    return null;
  };

  return (
    <div className="flex justify-center items-start pt-16 pb-8 px-4">
      <Card className="w-full max-w-2xl">
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{tLogin.createYourAccount}</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {tLogin.joinAndManage}
          </p>
        </div>

        <form onSubmit={handleFormSubmit} autoComplete="on" className="mt-8">
          {renderForm()}
        </form>

        <div className="flex justify-center items-center mt-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {tLogin.alreadyHaveAccountQuestion}{' '}
            <span
              onClick={() => history.push('/login')}
              className="text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
            >
              {tLogin.signIn}
            </span>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default RegisterForm;
